import { PurchasedGoodsInsights } from '@shared/api/types';
import { DashboardGrid } from '@shared/components/dashboard/dashboard-grid';
import { MetricCard } from '@shared/components/dashboard/metric-card';
import { MetricTrend } from '@shared/components/dashboard/metric-trend';
import { Badge } from '@shared/components/ui/badge';
import SmallNumber from '@shared/components/content/small-number';
import { formatCompact, getCurrencySymbol } from '@shared/lib/format';
import { formatSmallNumber } from '@shared/lib/utils';
import { useInsightsPreviousPeriod } from '../hooks/use-insights-previous-period';
import {
  getPercentageChange,
  getPreviousYearMetrics,
} from '../lib/insights-yoy';

// Placeholder copy — final wording lands in the project's copy pass.
const COST_UPSELL = 'Unlock combined spend + carbon insights';
const ADD_SPEND = 'Add spend data to unlock cost + carbon insights';

type Props = {
  data?: PurchasedGoodsInsights;
  currency?: string | null;
  /** Whether the org has the cost feature; false → spend cards render locked. */
  hasCostFeature: boolean;
  /** Whether any dimension filter is active — shows the %-of-total badges. */
  isFiltered: boolean;
  isLoading?: boolean;
};

/**
 * Compact for large figures; small ones keep two decimals with a "<0.01"
 * floor, so a 0.3 t scope reads "0.3" rather than rounding to a false zero.
 */
const formatCompactSmall = (value: number) => {
  // A genuine zero is "0" — "0.00" implies rounding that didn't happen.
  if (value === 0) return '0';
  return value >= 1000 ? formatCompact(value) : formatSmallNumber(value);
};

/** "% of total" chip for a filtered scope, on the value line after the unit. */
function ProportionBadge({ value }: { value: number | null | undefined }) {
  if (value == null) return undefined;
  const rounded = Math.round(value * 100);
  // A tiny share reads "<1%", never a misleading "0%".
  const figure = rounded === 0 && value > 0 ? '<1' : `${rounded}`;
  return (
    <Badge
      variant="default"
      size="sm"
      className="border-transparent bg-primary-50 text-primary-800"
    >
      {figure}% of total
    </Badge>
  );
}

/**
 * The four headline metrics: Emissions, Carbon intensity, Spend, and Emissions
 * per spend. Values recompute against the active filter scope; when filtered,
 * the absolute cards carry a %-of-total badge. The two spend-dependent cards
 * render locked (with an upsell) when the org lacks the cost feature, and
 * blank with an "add spend data" prompt when the feature is on but no spend
 * has been shared.
 */
export function InsightsKpiRow({
  data,
  currency,
  hasCostFeature,
  isFiltered,
  isLoading,
}: Props) {
  const symbol = getCurrencySymbol(currency) || '£';

  const emissions = data?.emissionsTonnes ?? null;
  const emissionsPerKg = data?.emissionsPerKg ?? null;
  const spend = data?.spend != null ? Number(data.spend) : null;
  const emissionsPerSpend = data?.emissionsPerSpendUnit ?? null;
  const purchases = data?.purchases ?? null;
  const weightKg = data?.weightKg ?? null;

  const noSpendData = hasCostFeature && spend == null;

  const previousYear = getPreviousYearMetrics(data?.previousYear);
  const { name: previousPeriodName, spansMultipleYears } =
    useInsightsPreviousPeriod(data?.timePeriodStart, data?.timePeriodEnd);
  // A window spanning several reporting years has no single year to compare to.
  const comparisonLabel =
    previousPeriodName && !spansMultipleYears
      ? `Compared with the same months in ${previousPeriodName}${
          isFiltered ? ', with the same filters applied' : ''
        }`
      : null;

  /**
   * Year-on-year line for a card. Omitted entirely — rather than left as an
   * empty row — when there's nothing sound to compare against.
   */
  const yearOnYear = (
    current: number | null,
    previous: number | null,
    variant: 'impact' | 'neutral' = 'impact'
  ) => {
    if (!comparisonLabel) return undefined;
    const percentChange = getPercentageChange(current, previous);
    if (percentChange == null) return undefined;
    return (
      <MetricTrend
        percentChange={percentChange}
        label={comparisonLabel}
        variant={variant}
      />
    );
  };

  return (
    <DashboardGrid className="lg:grid-cols-4">
      <MetricCard
        variant="default"
        title="Emissions"
        value={
          isLoading || emissions == null ? undefined : (
            <SmallNumber value={emissions} formatter={formatCompactSmall} />
          )
        }
        unit="tCO₂e"
        badge={
          isFiltered ? (
            <ProportionBadge value={data?.proportionEmissions} />
          ) : undefined
        }
        description={
          weightKg != null
            ? `From ${formatCompactSmall(weightKg / 1000)} tonnes purchased`
            : undefined
        }
        headerAction={yearOnYear(emissions, previousYear.emissionsTonnes)}
      />
      <MetricCard
        variant="default"
        title="Carbon intensity"
        value={
          isLoading || emissionsPerKg == null ? undefined : (
            <SmallNumber value={emissionsPerKg} />
          )
        }
        unit="kgCO₂e/kg"
        description="Emissions per kg purchased"
        headerAction={yearOnYear(emissionsPerKg, previousYear.emissionsPerKg)}
      />
      <MetricCard
        variant="default"
        title="Spend"
        locked={!hasCostFeature}
        lockedDescription={COST_UPSELL}
        value={
          isLoading || spend == null
            ? undefined
            : spend > 0 && spend < 1
              ? `<${symbol}1`
              : `${symbol}${formatCompact(spend)}`
        }
        badge={
          isFiltered ? (
            <ProportionBadge value={data?.proportionSpend} />
          ) : undefined
        }
        description={
          noSpendData
            ? ADD_SPEND
            : purchases != null
              ? `Across ${purchases.toLocaleString('en-GB')} purchases`
              : undefined
        }
        // Spend rising or falling is not itself good or bad, so it stays muted.
        headerAction={yearOnYear(spend, previousYear.spend, 'neutral')}
      />
      <MetricCard
        variant="default"
        title="Emissions per spend"
        locked={!hasCostFeature}
        lockedDescription={COST_UPSELL}
        value={
          isLoading || emissionsPerSpend == null ? undefined : (
            <SmallNumber value={emissionsPerSpend} />
          )
        }
        unit={`kgCO₂e/${symbol}`}
        description={noSpendData ? ADD_SPEND : `Emissions per ${symbol} spent`}
        headerAction={yearOnYear(
          emissionsPerSpend,
          previousYear.emissionsPerSpendUnit
        )}
      />
    </DashboardGrid>
  );
}
